import React, { useMemo, useState } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { Box, Chip, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import CountryTabs from "../components/CountryTabs";
import { getCategoryTheme } from "../categoryThemes";
import {
  CATEGORIES,
  CountryCode,
  getVendorCountForCategory,
} from "../mockData/marketplaceMockData";
import "../Marketplace.scss";

const MarketplaceHome: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const country = (searchParams.get("country") as CountryCode) || "IN";

  const handleCountryChange = (code: CountryCode) => {
    setSearchParams({ country: code });
  };

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/home-employee/marketplace/category/${categoryId}?country=${country}`);
  };

  const categoryStats = useMemo(() => {
    const activeCategories = CATEGORIES.filter(
      (category) => getVendorCountForCategory(category.id, country) > 0
    );
    const totalVendors = activeCategories.reduce(
      (sum, category) => sum + getVendorCountForCategory(category.id, country),
      0
    );

    return {
      activeCategories: activeCategories.length,
      totalVendors,
    };
  }, [country]);

  const featuredCategories = useMemo(() => {
    return [...CATEGORIES]
      .filter((category) => getVendorCountForCategory(category.id, country) > 0)
      .sort(
        (a, b) =>
          getVendorCountForCategory(b.id, country) - getVendorCountForCategory(a.id, country)
      )
      .slice(0, 4);
  }, [country]);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return CATEGORIES;
    const query = search.toLowerCase();
    return CATEGORIES.filter(
      (category) =>
        category.name.toLowerCase().includes(query) ||
        category.description.toLowerCase().includes(query) ||
        category.subCategories.some((sub) => sub.toLowerCase().includes(query))
    );
  }, [search]);

  const featuredCategoryIds = useMemo(
    () => new Set(featuredCategories.map((category) => category.id)),
    [featuredCategories]
  );

  const allCategories = useMemo(() => {
    const baseList = search.trim()
      ? filteredCategories
      : filteredCategories.filter((category) => !featuredCategoryIds.has(category.id));

    return baseList;
  }, [filteredCategories, featuredCategoryIds, search]);

  const renderCategoryCard = (category: (typeof CATEGORIES)[number], featured = false) => {
    const vendorCount = getVendorCountForCategory(category.id, country);
    const theme = getCategoryTheme(category.id);
    const visibleTags = category.subCategories.slice(0, 1);
    const hiddenTagCount = Math.max(category.subCategories.length - visibleTags.length, 0);

    return (
      <Box
        key={category.id}
        className={`category_card ${featured ? "featured" : ""} ${vendorCount === 0 ? "empty" : ""}`}
        style={
          {
            "--category-accent": theme.accent,
            "--category-accent-soft": theme.accentSoft,
            "--category-gradient": theme.gradient,
          } as React.CSSProperties
        }
        onClick={() => vendorCount > 0 && handleCategoryClick(category.id)}
      >
        <Box className="category_card_top">
          <Box className="category_icon_badge">
            <span className="category_icon">{category.icon}</span>
          </Box>
          {vendorCount > 0 && (
            <span className="category_arrow" aria-hidden="true">
              <ArrowForwardIcon />
            </span>
          )}
        </Box>

        <h6>{category.name}</h6>
        <p>{category.description}</p>

        <Box className="category_meta">
          <Chip
            size="small"
            label={
              vendorCount > 0
                ? `${vendorCount} partner${vendorCount > 1 ? "s" : ""}`
                : "Coming soon"
            }
            className={vendorCount > 0 ? "count_chip" : "soon_chip"}
          />
        </Box>

        {visibleTags.length > 0 && (
          <Box className="subcategory_tags">
            {visibleTags.map((sub) => (
              <span key={sub} className="sub_tag">
                {sub}
              </span>
            ))}
            {hiddenTagCount > 0 && <span className="sub_tag more">+{hiddenTagCount}</span>}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box className="marketplace_wrapper marketplace_home">
      <Box className="marketplace_hero">
        <Box className="marketplace_hero_inner">
          <Box className="marketplace_hero_content">
            <span className="marketplace_hero_eyebrow">Employee benefits</span>
            <Box className="marketplace_hero_title_row">
              <h1>
                Market<span>place</span>
              </h1>
              <Box className="marketplace_hero_stats">
                <Box className="hero_stat">
                  <CategoryOutlinedIcon />
                  <span>
                    <strong>{categoryStats.activeCategories}</strong> categories
                  </span>
                </Box>
                <Box className="hero_stat">
                  <StorefrontOutlinedIcon />
                  <span>
                    <strong>{categoryStats.totalVendors}</strong> partners
                  </span>
                </Box>
              </Box>
            </Box>
            <p>Curated wellness, health, and lifestyle services for your team.</p>
          </Box>

          <Box className="marketplace_hero_controls">
            <CountryTabs activeCountry={country} onChange={handleCountryChange} variant="hero" />
            <TextField
              fullWidth
              size="small"
              placeholder="Search categories or services..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="marketplace_search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {featuredCategories.length > 0 && !search.trim() && (
          <Grid xs={12}>
            <Box className="marketplace_section_header">
              <h6>Popular right now</h6>
              <p>Most partner options in {country === "IN" ? "India" : "Dubai"}</p>
            </Box>
            <Box className="category_grid featured_grid">
              {featuredCategories.map((category) => renderCategoryCard(category, true))}
            </Box>
          </Grid>
        )}

        <Grid xs={12}>
          <Box className="marketplace_section_header">
            <h6>{search.trim() ? "Search results" : "All categories"}</h6>
            <p>
              {allCategories.length} categor{allCategories.length === 1 ? "y" : "ies"} found
            </p>
          </Box>

          {allCategories.length === 0 ? (
            <Box className="empty_state">
              <p>No categories match your search.</p>
            </Box>
          ) : (
            <Box className="category_grid">
              {allCategories.map((category) => renderCategoryCard(category))}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarketplaceHome;
